import React from "react";
import { useLocation } from "react-router-dom";

// chakra imports
import {
  Box,
  Flex,
  Drawer,
  DrawerBody,
  Icon,
  useColorModeValue,
  DrawerOverlay,
  useDisclosure,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import Content from "components/sidebar/components/Content";
import SidebarUserMenu from "components/sidebar/components/SidebarUserMenu";
import {
  renderThumb,
  renderTrack,
  renderSidebarView,
  renderView,
} from "components/scrollbar/Scrollbar";
import { Scrollbars } from "react-custom-scrollbars-2";
import PropTypes from "prop-types";

// Assets
import { IoMenuOutline } from "react-icons/io5";

function Sidebar(props) {
  const { routes, collapsed } = props;

  let variantChange = "0.3s ease";
  let shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.08)",
    "unset"
  );
  // Chakra Color Mode
  let sidebarBg = useColorModeValue("white", "navy.800");
  let sidebarMargins = "0px";
  let footerBorderColor = useColorModeValue("gray.200", "whiteAlpha.200");

  // SIDEBAR — el contenedor fijo debe ocupar solo el ancho del panel, no toda la pantalla
  return (
    <Box
      display={{ sm: 'none', xl: 'block' }}
      position="fixed"
      top={0}
      left={0}
      w={collapsed ? '70px' : '300px'}
      h="100vh"
      zIndex={1100}
      pointerEvents="none"
    >
      <Box
        bg={sidebarBg}
        transition={variantChange}
        w="100%"
        h="100vh"
        m={sidebarMargins}
        minH="100%"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        boxShadow={shadow}
        pointerEvents="auto"
      >
        <Box
          flex='1'
          minH={0}
          overflow={collapsed ? 'auto' : 'hidden'}
          overflowX='hidden'
          w="100%"
        >
          {collapsed ? (
            <Content routes={routes} collapsed={collapsed} />
          ) : (
            <Scrollbars
              autoHide
              style={{ height: '100%' }}
              renderTrackVertical={renderTrack}
              renderThumbVertical={renderThumb}
              renderView={renderSidebarView}
            >
              <Content routes={routes} collapsed={collapsed} />
            </Scrollbars>
          )}
        </Box>
        <Box
          flexShrink={0}
          borderTopWidth="1px"
          borderColor={footerBorderColor}
          px={0}
          py={2}
          bg={sidebarBg}
          w="100%"
          display="flex"
          justifyContent="center"
        >
          <SidebarUserMenu collapsed={collapsed} />
        </Box>
      </Box>
    </Box>
  );
}

// FUNCTIONS
export function SidebarResponsive(props) {
  let sidebarBackgroundColor = useColorModeValue("white", "navy.800");
  let menuColor = useColorModeValue("gray.700", "white");
  let menuHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  let drawerFooterBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();
  const location = useLocation();

  const { routes, sidebarCollapsed, setSidebarCollapsed } = props;

  React.useEffect(() => {
    if (isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cerrar al cambiar de ruta
  }, [location.pathname]);

  const handleToggle = () => {
    if (window.innerWidth >= 1280) {
      setSidebarCollapsed && setSidebarCollapsed(!sidebarCollapsed);
    } else {
      onOpen();
    }
  };

  return (
    <>
      <Flex
        ref={btnRef}
        as="button"
        type="button"
        aria-label="Abrir menú"
        alignItems="center"
        justifyContent="center"
        w="40px"
        h="40px"
        borderRadius="md"
        flexShrink={0}
        onClick={handleToggle}
        _hover={{ bg: menuHoverBg }}
      >
        <Icon
          as={IoMenuOutline}
          color={menuColor}
          w="24px"
          h="24px"
        />
      </Flex>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={document.documentElement.dir === "rtl" ? "right" : "left"}
        finalFocusRef={btnRef}
       closeOnOverlayClick={false}>
        <DrawerOverlay />
        <DrawerContent w='285px' maxW='285px' bg={sidebarBackgroundColor}>
          <DrawerCloseButton
            zIndex='3'
            onClose={onClose}
            _focus={{ boxShadow: "none" }}
            _hover={{ boxShadow: "none" }}
          />
          <DrawerBody
            maxW='285px'
            px='0'
            pb='0'
            display='flex'
            flexDirection='column'
            overflow='hidden'>
            <Box flex='1' minH={0} overflow='hidden'>
              <Scrollbars
                autoHide
                style={{ height: '100%' }}
                renderTrackVertical={renderTrack}
                renderThumbVertical={renderThumb}
                renderView={renderView}>
                <Content routes={routes} isDrawer />
              </Scrollbars>
            </Box>
            <Box
              flexShrink={0}
              borderTopWidth='1px'
              borderColor={drawerFooterBorder}
              px={3}
              py={2}
            >
              <SidebarUserMenu collapsed={false} />
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
// PROPS

Sidebar.propTypes = {
  logoText: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.string,
};

export default Sidebar;
